import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { FeaturedPlayer } from "@/components/FeaturedPlayer";
import { guestService } from "@/services/guestService";
import { useAuth } from "@/context/AuthContext";
import { Play, Sparkles, ArrowRight, Clapperboard } from "lucide-react";

export default function Theater() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    guestService.publicPlaylist().then((list) => {
      setCourses(list);
      const requested = params.get("course");
      const initial = list.find((c) => c.id === requested)?.id || list[0]?.id || null;
      setSelected(initial);
    }).catch(() => {});
  }, [params]);

  const active = courses.find((c) => c.id === selected);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Theater hero */}
      <section className="border-b border-border" data-testid="theater-page">
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-12 pb-10">
          <div className="flex items-center gap-2 mb-3">
            <Clapperboard className="h-4 w-4 text-primary" />
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary">Watch &amp; Learn — Free</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.03] max-w-3xl" data-testid="theater-title">
            See what Learn Me makes — <span className="text-primary">no signup required.</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Real, hand-picked courses. Press play, watch a narrated explainer come to life, then make one on any topic you like.
          </p>

          {courses.length === 0 ? (
            <div className="mt-10 aspect-video max-w-3xl mx-auto bg-secondary animate-pulse" data-testid="theater-empty" />
          ) : (
            <motion.div key={selected} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="mt-10 flex flex-col items-center" data-testid="theater-stage">
              {selected && (
                <FeaturedPlayer
                  courseId={selected}
                  eyebrow={active ? `Now playing · ${active.length_label}` : "Now playing"}
                  maxWidthClass="max-w-3xl w-full mx-auto"
                />
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Playlist carousel */}
      {courses.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-8 py-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold tracking-tight">The playlist</h2>
            <span className="text-sm text-muted-foreground">{courses.length} free course{courses.length > 1 ? "s" : ""}</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="theater-carousel">
            {courses.map((c) => {
              const isActive = c.id === selected;
              return (
                <button
                  key={c.id}
                  onClick={() => { setSelected(c.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid={`theater-item-${c.id}`}
                  className={`group snap-start shrink-0 w-64 text-left border bg-card overflow-hidden transition-colors ${isActive ? "border-primary ring-2 ring-primary" : "border-border hover:border-foreground"}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-[#160f04] to-[#2c2110] relative flex items-center justify-center">
                    <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 20% 30%, rgba(245,190,90,0.6), transparent 45%), radial-gradient(circle at 85% 80%, rgba(212,160,60,0.4), transparent 40%)" }} />
                    <span className={`relative h-11 w-11 rounded-full flex items-center justify-center transition-colors ${isActive ? "bg-amber-200" : "bg-amber-300/90 group-hover:bg-amber-200"}`}>
                      <Play className="h-5 w-5 text-[#1a1206] ml-0.5" />
                    </span>
                    <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-wider font-bold text-amber-300/90">{c.length_label}</span>
                    {isActive && <span className="absolute top-2 right-3 text-[10px] uppercase tracking-wider font-bold text-primary bg-white/90 px-1.5 py-0.5">Now playing</span>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-sm font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors">{c.title}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.subtitle}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Call to action */}
      <section className="border-t border-border bg-foreground text-background text-grain" data-testid="theater-cta">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Your turn
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">
              Loved it? Now make one on <span className="text-primary">any topic.</span>
            </h2>
            <p className="mt-3 text-background/70">
              Type a subject and Learn Me builds you a narrated, chaptered course in seconds — saved to your library forever.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => navigate(user ? "/learn" : "/register")}
              className="h-12 px-8 bg-primary text-white font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              data-testid="theater-cta-primary"
            >
              {user ? "Create a course" : "Get started free"} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate("/")}
              className="h-12 px-8 border border-background/40 text-background font-semibold hover:bg-background/10 transition-colors"
              data-testid="theater-cta-secondary"
            >
              Try a free 2-min demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
